import { type Identifier, useGetOne, useTranslate } from "ra-core";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

import { ListErrorState } from "../design/ListStates";
import type { Deal } from "../types";
import { DealProfileSection } from "./DealProfileSection";

export interface DealProfileSheetProps {
  dealId: Identifier;
  /** The lead's name, already known by the caller (Hoy card) -- shown as the sheet title without waiting on the deal fetch below. */
  leadName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Almikia Epic EF (TASK-122), vision #5: mobile has no lead screen (QEA11),
 * so this sheet is how an asesora reaches a lead's 'Perfil del cliente' from
 * her Hoy card -- the same `DealProfileSection` (TASK-119) the lead show
 * mounts, unchanged, fetching only the parent lead it needs to render (the
 * profile and catalogue data load lazily inside the section itself, only
 * while the sheet is open).
 *
 * Each Hoy card mounts its own `DealProfileSheet`, so a mounted sheet's
 * `dealId` never actually changes here. The section's own `deal.id`-keyed
 * remount fix (TASK-119 retry round) is kept as a safety net regardless,
 * since `DealProfileSection` is also reached from the lead show, where a
 * single sheet-less instance CAN be handed a different `deal.id` across
 * navigations.
 */
export function DealProfileSheet({
  dealId,
  leadName,
  open,
  onOpenChange,
}: DealProfileSheetProps) {
  const translate = useTranslate();
  const {
    data: deal,
    isPending,
    error,
    refetch,
  } = useGetOne<Deal>("deals", { id: dealId }, { enabled: open });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="deal-profile-sheet"
        side="bottom"
        className="h-dvh flex flex-col [&_[data-slot=sheet-close]]:text-sidebar-foreground"
        aria-describedby={undefined}
      >
        <SheetHeader className="bg-sidebar text-sidebar-foreground border-b">
          <SheetTitle className="text-sidebar-foreground text-xl font-semibold">
            {leadName}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 max-w-[390px] w-full mx-auto">
          {isPending ? (
            <div aria-busy="true" role="status" className="space-y-3">
              <span className="sr-only">{translate("crm.common.loading")}</span>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : error || !deal ? (
            <ListErrorState
              message={translate("resources.deals.deal_profile.error_loading", {
                _: "No se pudo cargar el perfil",
              })}
              onRetry={() => refetch()}
            />
          ) : (
            <DealProfileSection deal={deal} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
